import { type ReactQuery } from 'cms-api'

import { rem } from 'polished'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { Navigation, Pagination, EffectFade, Autoplay, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  BULLET_WRAPPER_SIZE_PX,
  CarouselNavigation,
} from 'components/CarouselNavigation'
import { SaveMore } from 'components/Icon/icons/SaveMore'
import { ShopFaster } from 'components/Icon/icons/ShopFaster'
import { StressLess } from 'components/Icon/icons/StressLess'
import { Box, Container, Flex, Grid } from 'components/Layout'

import { Text } from 'components/Typography'
import { SCHEMA_TYPES, WIDGET_PLACEMENT } from 'constants/common'
import {
  COLOR_INTENT,
  COLOR_NAMES,
  FONT_SIZE,
  FONT_STACK,
  SPACE,
  Z_INDEX,
} from 'Theme'
import { WidgetTitle } from '../WidgetTitle'

const BULLET_THRESHOLD = 10
const BULLET_ACTIVE_CLASS = 'trade-benefits-pagination-bullet-active'
const AUTOPLAY_DELAY = 10000

const StyledSwiper = styled(Swiper)`
  user-select: none;
  height: 100%;
  .swiper-slide {
    height: auto;
  }
  .swiper-pagination-bullet {
    transform: scale(1) !important;
    transition: all 600ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .${BULLET_ACTIVE_CLASS} {
    transform: scale(1.5) !important;
    background-color: ${COLOR_NAMES.PINEY_LAKE};
    opacity: 1;
  }

  .swiper-pagination-bullets {
    min-width: ${rem(
      BULLET_WRAPPER_SIZE_PX * BULLET_THRESHOLD
    )}; // bullet wrapper size*max number of bullets visible at once
    column-gap: 20px;
    padding: 16px;
    position: relative;
  }
`

const AdvantageItem = ({ icon, title, description }) => (
  <Flex justifyContent="center">
    <Flex flexDirection="column" alignItems="center" maxWidth={rem(230)}>
      {icon}
      <Text
        fontFamily={FONT_STACK.SERIF}
        fontSize={{ MOBILE: rem(24), TABLET: rem(24) }}
        color={COLOR_INTENT.WIDGETS.STRAPI_TRADE_ADVANTAGES_WIDGET.ITEM.TITLE}
        mt={rem(16)}
      >
        {title}
      </Text>
      <Box width={rem(52)} height={rem(1)} bg="#949494" my={rem(16)} />
      <Text
        color={COLOR_INTENT.WIDGETS.STRAPI_TRADE_ADVANTAGES_WIDGET.ITEM.TITLE}
        fontSize={rem(16)}
        textAlign="center"
        lineHeight="140%"
      >
        {description}
      </Text>
    </Flex>
  </Flex>
)

export const StrapiTradeAdvantagesWidget: React.FC<
  React.PWC<ReactQuery.WidgetsTradeAdvantagesComponent>
> = ({ title }) => {
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)
  const paginationRef = useRef(null)
  const swiper = useRef(null)
  return (
    <Box
      pb={{ MOBILE: SPACE.PX_48, TABLET: rem(66) }}
      pt={{ MOBILE: SPACE.PX_64, TABLET: rem(66) }}
    >
      <Container>
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
        >
          {title && (
            <Box maxWidth={rem(508)}>
              <WidgetTitle
                color={
                  COLOR_INTENT.WIDGETS.STRAPI_TRADE_ADVANTAGES_WIDGET.TITLE
                }
                fontFamily={FONT_STACK.SERIF}
                placement={WIDGET_PLACEMENT.SECTION}
                textAlign="center"
                fontSize={{ MOBILE: FONT_SIZE.PX_32, TABLET: rem(40) }}
                lineHeight={{ MOBILE: rem(44), TABLET: rem(52) }}
                mb={{ MOBILE: rem(40), TABLET: rem(50) }}
              >
                {title}
              </WidgetTitle>
            </Box>
          )}
        </Flex>
        <Grid
          gridTemplateColumns="repeat(3, 1fr)"
          gap={rem(32)}
          display={{ MOBILE: 'none', TABLET: 'grid' }}
        >
          <AdvantageItem
            icon={<SaveMore />}
            title="Save more."
            description="The best trade discount is listed on every product page—no surprises."
          />
          <AdvantageItem
            icon={<ShopFaster />}
            title="Shop faster."
            description="Fill your cart with all your favorite brands, consolidated in one place."
          />
          <AdvantageItem
            icon={<StressLess />}
            title="Stress less."
            description="Track your orders from multiple vendors all in one place."
          />
        </Grid>
      </Container>
      <Box display={{ MOBILE: 'block', TABLET: 'none' }} mt={SPACE.PX_20}>
        {' '}
        <StyledSwiper
          ref={swiper}
          modules={[Autoplay, Navigation, Pagination, EffectFade, A11y]}
          navigation={{
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          }}
          speed={750}
          pagination={{
            el: paginationRef.current,
            clickable: true,
            bulletActiveClass: BULLET_ACTIVE_CLASS,
          }}
          slidesPerView={1}
          direction="horizontal"
          loop
          autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
          allowTouchMove
          itemScope
          itemType={SCHEMA_TYPES.ITEM_LIST}
        >
          <SwiperSlide>
            <AdvantageItem
              icon={<SaveMore />}
              title="Save more."
              description="The best trade discount is listed on every product page—no surprises."
            />
          </SwiperSlide>
          <SwiperSlide>
            <AdvantageItem
              icon={<ShopFaster />}
              title="Shop faster."
              description="Fill your cart with all your favorite brands, consolidated in one place."
            />
          </SwiperSlide>
          <SwiperSlide>
            <AdvantageItem
              icon={<StressLess />}
              title="Stress less."
              description="Track your orders from multiple vendors all in one place."
            />
          </SwiperSlide>

          <Flex
            position="relative"
            width="100%"
            px={SPACE.PX_16}
            justifyContent="center"
            zIndex={Z_INDEX.CAROUSEL_NAVIGATION_WRAPPER}
            mt={rem(20)}
          >
            <CarouselNavigation
              prevButtonRef={prevButtonRef}
              nextButtonRef={nextButtonRef}
              paginationRef={paginationRef}
              hasArrows={false}
            />
          </Flex>
        </StyledSwiper>
      </Box>
    </Box>
  )
}
